import { useMemo, useState } from "react";
import { TrendingUp, TrendingDown, Minus, ChevronDown, ChevronRight, MessageSquareOff } from "lucide-react";
import { resumoEfeito, efeitoPorVendedor, ROTULO_FOCO, MINIMO_POR_LADO } from "../lib/efeitoIa";

// ═══════════════════════════════════════════════════════════════
// O AJUSTE ESTÁ FUNCIONANDO? — a pergunta que fecha o ciclo
// ═══════════════════════════════════════════════════════════════
//
// A IA lê a conversa e recomenda. Aqui se cobra o resultado: quem recebeu a
// recomendação passou a receber mais resposta do fornecedor?
//
// A tela é dura com a própria amostra de propósito. Abaixo de cinco conversas
// analisadas de cada lado ela NÃO mostra porcentagem — diz que ainda está
// juntando. Número pequeno apresentado como tendência faz a gerência mudar o
// que estava dando certo.

export default function EfeitoIa({ partners = [], role = "comercial" }) {
  const [aberto, setAberto] = useState(false);
  const gerencial = role === "gerente" || role === "admin";

  const r = useMemo(() => resumoEfeito(partners), [partners]);
  const vendedores = useMemo(() => efeitoPorVendedor(partners), [partners]);

  if (!gerencial || r.analisadas === 0) return null;

  return (
    <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", borderLeft: "4px solid #0d9488", padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <TrendingUp size={15} color="#0d9488" />
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
            {r.comRecomendacao} de {r.analisadas} conversas geraram um ajuste
          </div>
          <div style={{ fontSize: 10.5, color: "#64748b" }}>
            {r.comLeitura > 0
              ? `${r.melhorou} de ${r.comLeitura} ${r.comLeitura === 1 ? "pessoa melhorou" : "pessoas melhoraram"} a taxa de resposta depois`
              : `ainda juntando conversas para saber se está surtindo efeito`}
            {r.colou > 0 && ` · ${r.colou} com texto padrão colado`}
          </div>
        </div>
        <button onClick={() => setAberto((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
          {aberto ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Detalhe
        </button>
      </div>

      {aberto && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
          {/* O que mais se repete é assunto de treino, não de recado
              individual — e é a leitura que já vale com pouca amostra. */}
          {r.focos.length > 0 && (
            <>
              <Titulo>O que mais se repete</Titulo>
              {r.focos.slice(0, 4).map((f) => (
                <div key={f.foco} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 11.5, color: "#475569" }}>
                  <span style={{ fontWeight: 700, color: "#0f172a", minWidth: 26 }}>{f.total}×</span>
                  <span style={{ flex: 1 }}>{ROTULO_FOCO[f.foco] || f.foco}</span>
                  <span style={{ fontSize: 10, color: "#94a3b8" }}>{f.pessoas} {f.pessoas === 1 ? "pessoa" : "pessoas"}</span>
                </div>
              ))}
            </>
          )}

          {r.robotica > 0 && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 7, fontSize: 10.5, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 7, padding: "8px 10px", margin: "10px 0", lineHeight: 1.45 }}>
              <MessageSquareOff size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                <strong>{r.robotica}</strong> {r.robotica === 1 ? "conversa soou" : "conversas soaram"} mecânica — texto de folheto,
                bloco grande, mesma fala que serviria para qualquer fornecedor. É o tipo de mensagem que não se lê até o fim.
              </span>
            </div>
          )}

          <Titulo>Antes e depois, por pessoa</Titulo>
          {vendedores.length === 0 && (
            <div style={{ fontSize: 11, color: "#94a3b8" }}>Ninguém recebeu recomendação ainda.</div>
          )}
          {vendedores.map((v) => (
            <div key={v.autor} style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 0", borderTop: "1px solid #f8fafc", flexWrap: "wrap" }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: "#0f172a", flex: 1, minWidth: 110 }}>{v.autor}</span>
              {v.lendo ? (
                <>
                  <span style={{ fontSize: 11, color: "#64748b" }}>
                    resposta do fornecedor: <strong style={{ color: "#94a3b8" }}>{v.antes.respondeu}%</strong>
                    {" → "}
                    <strong style={{ color: "#0f172a" }}>{v.depois.respondeu}%</strong>
                  </span>
                  <Seta delta={v.delta} />
                </>
              ) : (
                <span style={{ fontSize: 10.5, color: "#94a3b8" }}>
                  juntando conversas ({v.antes.n} antes, {v.depois.n} depois — precisa de {MINIMO_POR_LADO} de cada lado)
                </span>
              )}
            </div>
          ))}

          <div style={{ fontSize: 9.5, color: "#94a3b8", marginTop: 10, lineHeight: 1.5 }}>
            A comparação é antes e depois da primeira recomendação de cada pessoa. Ela não prova causa —
            mês fraco, região nova e sazonalidade mexem no mesmo número. Serve para levantar a pergunta na
            reunião, não para fechá-la.
          </div>
        </div>
      )}
    </div>
  );
}

const Titulo = ({ children }) => (
  <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em", margin: "10px 0 4px" }}>{children}</div>
);

function Seta({ delta }) {
  if (delta === null || delta === 0) return <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10.5, color: "#94a3b8" }}><Minus size={12} /> igual</span>;
  const sobe = delta > 0;
  const cor = sobe ? "#059669" : "#dc2626";
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10.5, fontWeight: 700, color: cor }}>
      {sobe ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {sobe ? "+" : ""}{delta} p.p.
    </span>
  );
}
